import { ComponentProps, memo } from 'react';

import {
  CopilotAction,
  CopilotDropdown,
  CopilotDropdownActionParams,
} from 'components/copilotDropdown';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { ConversationCopilotDropdownTranslate } from './components/translate/conversationCopilotDropdownTranslate';

const isConversationTranslateDisabled = ({
  conversationContext: { conversation } = {},
  featureFlags,
}: CopilotDropdownActionParams): boolean => {
  const isEmailChannel =
    conversation?.initial_channel === 'email' ||
    conversation?.initial_channel === 'contactform';
  const isNonEmailTextualChannel =
    conversation?.initial_channel === 'messenger' ||
    conversation?.initial_channel === 'widgetchat' ||
    conversation?.initial_channel === 'twitter' ||
    conversation?.initial_channel === 'sms' ||
    conversation?.initial_channel === 'facebookmessenger' ||
    conversation?.initial_channel === 'whatsapp';

  if (isNonEmailTextualChannel) {
    return false;
  }

  if (isEmailChannel && featureFlags?.emailTranslation) {
    return false;
  }

  return true;
};

/** Object containing conversation copilot actions along with their metadata. */
export const conversationCopilotActions = {
  translate: {
    title: ({ conversationContext: { translationLanguageCode } = {} }) =>
      translationLanguageCode ? 'Change translation language' : 'Translate',
    icon: 'translate',
    commandName: 'conversation:translate',
    isVisible: ({ featureFlags }) => !!featureFlags?.agentCopilot,
    isDisabled: isConversationTranslateDisabled,
    content: <ConversationCopilotDropdownTranslate />,
  },

  toggleSummary: {
    title: ({ conversationContext }) =>
      conversationContext?.isSummaryVisible ? 'Hide summary' : 'Show summary',
    commandName: 'conversation:summary',
    icon: 'list-ul',
    isVisible: ({ featureFlags }) => !!featureFlags?.agentCopilot,
    isDisabled: ({ conversationContext }) =>
      !conversationContext?.conversation?.summary?.can_be_generated,
    action: ({ conversationContext, dropdownContext }) => {
      dropdownContext?.closeDropdown();
      conversationContext?.setIsSummaryVisible?.(
        !conversationContext?.isSummaryVisible,
      );
    },
  },
} as const satisfies Record<string, CopilotAction>;

type Props = {
  buttonProps?: ComponentProps<typeof CopilotDropdown>['buttonProps'];
};

/**
 * Represents a dropdown component designed to be integrated into a rich text
 * editor's toolbar as an addon, providing AI-powered functionalities to enhance
 * and refine the content within the editor.
 */
export const ConversationCopilotDropdown = memo(
  function ConversationCopilotDropdown(props: Props): JSX.Element | null {
    const featureFlags = useFeatureFlag(['agent-copilot']);

    if (!featureFlags.agentCopilot) {
      return null;
    }

    return (
      <CopilotDropdown
        buttonProps={props.buttonProps}
        icon="ai"
        fixed
        actions={conversationCopilotActions}
      />
    );
  },
);
